import { useState, useEffect, useCallback, useMemo } from 'react';
import { Chart } from 'react-google-charts';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { Icon } from '../components/SvgIcons';

const PALETTE = ['#4f46e5', '#16a34a', '#f59e0b', '#dc2626', '#0891b2', '#7c3aed', '#db2777', '#65a30d', '#ea580c', '#0d9488'];

const fmt = (n) => Number(n || 0).toLocaleString();

const Card = ({ children, style }) => (
  <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 14, padding: '1.25rem 1.5rem', ...style }}>{children}</div>
);

// A 3D Google pie chart with its built-in legend.
function PieSection({ title, items, emptyText = 'No consumption recorded.' }) {
  const nonZero = (items || []).filter(d => Number(d.value || 0) > 0);

  const chartData = useMemo(
    () => [['Name', 'Tokens'], ...nonZero.map(d => [d.label, Number(d.value || 0)])],
    [nonZero]
  );

  const options = {
    is3D: true,
    colors: PALETTE,
    backgroundColor: 'transparent',
    chartArea: { width: '92%', height: '88%' },
    legend: { position: 'right', alignment: 'center', textStyle: { color: '#64748b', fontSize: 12 } },
    pieSliceText: 'percentage',
    tooltip: { text: 'value' },
    fontName: 'inherit',
  };

  return (
    <Card>
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: 'var(--text-primary)' }}>{title}</h3>
      {nonZero.length === 0 ? (
        <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>{emptyText}</div>
      ) : (
        <Chart
          chartType="PieChart"
          data={chartData}
          options={options}
          width="100%"
          height="340px"
          loader={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading chart…</div>}
        />
      )}
    </Card>
  );
}

export default function TokenConsumption() {
  const { showToast } = useToast();
  const [period, setPeriod] = useState('month');
  const [tenantSlug, setTenantSlug] = useState('');
  const [tenants, setTenants] = useState([]);
  const [data, setData] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/superadmin/tenants')
      .then(({ data }) => setTenants(data.tenants || []))
      .catch(() => setTenants([]));
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const q = `period=${period}${tenantSlug ? `&tenant=${tenantSlug}` : ''}`;
      const { data } = await API.get(`/token-usage/dashboard?${q}`);
      setData(data.data);

      if (tenantSlug) {
        // A specific tenant is selected — load its college/department breakdown
        // (grouped by college_id / department_id on the token logs).
        const { data: bd } = await API.get(`/token-usage/breakdown?slug=${tenantSlug}&period=${period}`);
        setBreakdown(bd.data);
      } else {
        setBreakdown(null);
      }
    } catch {
      showToast('Failed to load token usage', 'error');
      setData(null);
      setBreakdown(null);
    } finally {
      setLoading(false);
    }
  }, [period, tenantSlug, showToast]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // College-wise (university tenant) or department-wise (other tenant) distribution.
  const breakdownData = useMemo(() => (breakdown?.rows || []).map(r => ({
    label: r.name || '—', value: Number(r.total_tokens)
  })), [breakdown]);

  // All-tenant distribution (shown only when no specific tenant is selected).
  const tenantData = useMemo(() => (data?.byTenant || []).map(t => ({
    label: t.university_name || t.tenant_slug, value: Number(t.total_tokens)
  })), [data]);

  const breakdownTitle = breakdown?.dimension === 'college'
    ? 'College-wise Token Usage'
    : 'Department-wise Token Usage';

  const summary = data?.summary || {};
  const selStyle = { padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.875rem', cursor: 'pointer' };

  return (
    <div style={{ padding: '2rem', minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <Icon name="ChartBar" size={22} /> Token Consumption
        </h1>
        <p style={{ margin: '0.3rem 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          AI token usage across all tenants. Token counts are estimated from prompt/response size.
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} style={selStyle}>
          <option value="today">Today</option>
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
          <option value="year">Last year</option>
          <option value="all">All time</option>
        </select>
        <select value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} style={selStyle}>
          <option value="">All Tenants</option>
          {tenants.map(t => <option key={t.slug} value={t.slug}>{t.university_name || t.slug}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
            {[
              { label: 'Total Tokens', value: summary.total_tokens, color: '#4f46e5' },
              { label: 'Requests', value: summary.total_requests, color: '#0891b2' },
            ].map(c => (
              <Card key={c.label}>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{c.label}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: c.color, marginTop: '0.3rem' }}>{fmt(c.value)}</div>
              </Card>
            ))}
          </div>

          {tenantSlug ? (
            // Specific tenant selected: college (university) or department (other) pie.
            <PieSection
              title={breakdownTitle}
              items={breakdownData}
              emptyText="No consumption recorded for this tenant."
            />
          ) : (
            // All Tenants: tenant-wise distribution pie.
            <PieSection
              title="Consumption by Tenant"
              items={tenantData}
              emptyText="No consumption recorded."
            />
          )}
        </>
      )}
    </div>
  );
}
